import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  MissingPriceConfigurationNestedRef,
  MissingPriceConfigurationReportItem,
  MissingPriceConfigurationTimezoneRef
} from '../models/missing-price-configuration-report.model';

interface ApiDataWrapper<T> {
  data: T;
}

interface ApiReference {
  id?: unknown;
  name?: unknown;
}

interface ApiMissingPriceConfigurationReportItem {
  reason?: unknown;
  commercial_zone?: ApiReference | null;
  commercial_condition?: ApiReference | null;
  timezone?: ApiReference | null;
  gap_start_date?: unknown;
  gap_end_date?: unknown;
}

/**
 * Reporte de huecos de configuración de precio en un rango de fechas
 * (`catalogs-api/v1/prices/missing-configurations`): combinaciones de zona comercial,
 * condición comercial y zona horaria sin configuración o con periodos sin cubrir.
 */
@Injectable({
  providedIn: 'root'
})
export class MissingPriceConfigurationsReportService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiBaseUrl}/${environment.apiPrefixes.catalogs}/${environment.apiVersions.catalogs}/prices/missing-configurations`;

  getReport(startDate: string, endDate: string): Observable<MissingPriceConfigurationReportItem[]> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http
      .get<ApiDataWrapper<ApiMissingPriceConfigurationReportItem[]>>(this.baseUrl, { params })
      .pipe(
        map(response => {
          const rows = Array.isArray(response?.data) ? response.data : [];
          return rows.map(row => this.normalizeRow(row));
        })
      );
  }

  private normalizeRow(row: ApiMissingPriceConfigurationReportItem): MissingPriceConfigurationReportItem {
    return {
      reason: this.toStringValue(row.reason, ''),
      commercial_zone: this.normalizeReference(row.commercial_zone),
      commercial_condition: this.normalizeReference(row.commercial_condition),
      timezone: this.normalizeTimezoneReference(row.timezone),
      gap_start_date: this.toStringValue(row.gap_start_date, ''),
      gap_end_date: this.toStringValue(row.gap_end_date, '')
    };
  }

  private normalizeReference(reference: ApiReference | null | undefined): MissingPriceConfigurationNestedRef | null {
    if (!reference || typeof reference !== 'object') {
      return null;
    }

    const id = this.toNumberValue(reference.id);
    const name = this.toStringValue(reference.name, 'N/D');

    return {
      id,
      name
    };
  }

  private normalizeTimezoneReference(reference: ApiReference | null | undefined): MissingPriceConfigurationTimezoneRef | null {
    const normalized = this.normalizeReference(reference);
    if (!normalized) {
      return null;
    }

    return normalized;
  }

  private toStringValue(value: unknown, fallback: string): string {
    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number') {
      return value.toString();
    }

    return fallback;
  }

  private toNumberValue(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }
}
